import {
    InstagramInfluencerUser,
    PostPaymentRequest,
    TiktokInfluencerPost,
    TiktokInfluencerUser,
    YouTubeInfluencerPost,
} from '@round/api';
import { Button, Popover } from '@round/ui-kit';
import { ReactComponent as WarningIcon } from 'assets/Danger.svg';
import styles from './CostPaymentButton.module.css';
import { asMoney } from 'helpers';
import { useContext, useEffect, useState } from 'react';
import { getRoundClientId } from '../PaymentRequestStatusCell/helpers';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import { InfluencerPlanContext } from 'Modules/Advertising/InfluencerPlan/contexts/InfluencerPlanContext';
import PostPaymentRequestModal from 'Modules/Plans/Project/ProjectDetails/Posts/PostPaymentRequestModal/PostPaymentRequestModal';
import { mapFormValuesToPatchData, PaymentDetailsPatchData } from '../helpers';
import { FormValues } from 'Modules/Plans/Project/ProjectDetails/Posts/PostPaymentRequestModal/helpers';
import { FormikErrors } from 'formik';
import { useClientProviderFeeConfigs, usePosts, useInvalidatePosts } from '@round/creatorbase-helpers/api-hooks';
import { InstagramInfluencerPost } from 'Modules/Advertising/InfluencerPlan/types/Instagram.types';
import { useCheckUserGroupsAccess } from 'Modules/Auth/hooks/useCheckUserGroupsAccess';

type Props = {
    roundPost: TiktokInfluencerPost | InstagramInfluencerPost | YouTubeInfluencerPost;
    influencer?: TiktokInfluencerUser | InstagramInfluencerUser | null;
    handlePostUpdate: (patchData: PaymentDetailsPatchData) => Promise<{
        success: boolean;
        errors?: FormikErrors<FormValues>;
    }>;
    onUpdated: (paymentRequest?: PostPaymentRequest) => void;
    isCostLocked?: boolean;
};

const CostPaymentButton = ({ roundPost, influencer, handlePostUpdate, onUpdated, isCostLocked }: Props) => {
    const { influencerPlan } = useContext(InfluencerPlanContext);
    const campaignCurrencyId = influencerPlan?.currency.id;

    const isPlannerOrFinance = useCheckUserGroupsAccess(['round_planner', 'round_finance'], 'any');

    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

    const { getCurrencyById } = useCurrencies();

    const {
        data: postData,
        isLoading: isPostDataLoading,
        isFetching: isPostDataFetching,
        isSuccess: isPostDataSuccess,
    } = usePosts(
        { id: roundPost.influencer_post_id ?? undefined, page_size: 1 },
        { enabled: isPaymentModalOpen && !!roundPost.influencer_post_id }
    );

    const invalidatePosts = useInvalidatePosts();

    const post = postData?.results[0];
    // This likely means the user doesn't have access to the post in Creatorbase, or the post doesn't exist.
    // Either way, it means we can't confirm the status of the payment request and should warn the user.
    const hasNoCreatorbasePostData = isPostDataSuccess && !post;
    const isPaymentModalOpenWithPostData = isPaymentModalOpen && !hasNoCreatorbasePostData;

    const [warningIconRef, setWarningIconRef] = useState<SVGSVGElement | null>(null);

    useEffect(() => {
        if (isPaymentModalOpen && hasNoCreatorbasePostData) {
            // Ensures the modal won't open unexpectedly if the data changes and the post becomes available.
            setIsPaymentModalOpen(false);
        }
    }, [isPaymentModalOpen, hasNoCreatorbasePostData]);

    const { data: feeConfigs } = useClientProviderFeeConfigs({ client_id: getRoundClientId() });

    return (
        <div onClick={(e) => e.stopPropagation()}>
            <Button
                className={styles.button}
                type="filled"
                htmlType="button"
                onClick={(e) => {
                    e.stopPropagation();
                    if (hasNoCreatorbasePostData) {
                        return;
                    }
                    setIsPaymentModalOpen(true);
                }}
                loading={isPostDataLoading || isPostDataFetching}
            >
                <>
                    <span>{asMoney(Number(roundPost.cost), getCurrencyById(roundPost.currency_id))}</span>
                    {hasNoCreatorbasePostData && (
                        <>
                            <WarningIcon className={styles.warningIcon} ref={setWarningIconRef} />
                            <Popover
                                anchorElement={warningIconRef}
                                showOn="hover"
                                options={{ modifiers: [{ name: 'offset', options: { offset: [0, 8] } }] }}
                            >
                                <p className={styles.warningTooltip}>
                                    Couldn't find cost data for this post. Please contact support.
                                </p>
                            </Popover>
                        </>
                    )}
                </>
            </Button>

            {post && (
                <PostPaymentRequestModal
                    isOpen={isPaymentModalOpenWithPostData}
                    onClose={() => setIsPaymentModalOpen(false)}
                    currencyId={campaignCurrencyId}
                    clientId={getRoundClientId()}
                    post={post}
                    updatePost={(formValues) =>
                        handlePostUpdate(mapFormValuesToPatchData(formValues, feeConfigs?.results))
                    }
                    onPaymentRequestUpdated={() => {
                        onUpdated();
                        if (!roundPost.influencer_post_id) {
                            return;
                        }
                        invalidatePosts({ id: roundPost.influencer_post_id, page_size: 1 }, { refetchType: 'active' });
                    }}
                    defaultValues={{
                        cost: Number(roundPost.cost),
                        paypalEmail: influencer?.paypal_email ?? undefined,
                    }}
                    shouldDisablePayment={!isPlannerOrFinance}
                    isCostLocked={isCostLocked}
                />
            )}
        </div>
    );
};

export default CostPaymentButton;
